import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import API from "config/ApiConfig";
import { Mosaic } from "react-loading-indicators";

// Constants
// const API_BASE_URL = 'http://localhost:5000/api/tentor';
const API_BASE_URL = API.API_TENTOR;
function getToken() {
  return localStorage.getItem('authToken');
}
function getIdTentor() {
  return localStorage.getItem('tentorId')
}
// Function to get authorization headers
function getAuthHeaders() {
    const JWT_TOKEN = getToken();
    return {
        'Authorization': `Bearer ${JWT_TOKEN}`,
        'Content-Type': 'application/json'
    };
}

export default function CardTable({ color }) {
  const [dataRekap, setDataRekap] = useState([]);
  const [siswaData, setSiswaData] = useState([]);
  const [tentorData, setTentorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [openDropdownId, setOpenDropdownId] = useState(null);

  // Handlers for form inputs
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  const handleDropdownToggle = (id) => {
    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try {
        const response = await fetch(`${API_BASE_URL}/rekap/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        if (response.ok) {
          setDataRekap(dataRekap.filter(item => item._id !== id));
          setOpenDropdownId(null); // Close the dropdown after deletion
        } else {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
      } catch (error) {
        setError('Failed to delete record.');
        console.error('Error deleting record:', error);
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const tentorId = localStorage.getItem('tentorId');
      try {
        // Fetching rekap data
        const rekapResponse = await fetch(`${API_BASE_URL}/rekap/${tentorId}`, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        if (!rekapResponse.ok) {
          throw new Error(`HTTP error! Status: ${rekapResponse.status}`);
        }
        const rekapData = await rekapResponse.json();
  
        // Fetching siswa data
        const siswaIds = [...new Set(rekapData.map(item => item.siswa_id))];
        const siswaRequests = siswaIds.map(id => 
          fetch(`${API_BASE_URL}/siswa/${id}`, {
            method: 'GET',
            headers: getAuthHeaders()
          }).then(response => {
            if (!response.ok) {
              throw new Error(`HTTP error! Status: ${response.status}`);
            }
            return response.json();
          })
        );
  
        const fetchedSiswaData = await Promise.all(siswaRequests);
        // Flatten the fetchedSiswaData array
        const flatSiswaData = fetchedSiswaData.flat();
  
        setDataRekap(rekapData);
        setSiswaData(flatSiswaData);
  
      } catch (error) {
        setError(error.message);
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, []);

  // Function to get siswa name by ID
  const getSiswaName = (siswaId) => {
    const siswa = siswaData.find(siswa => siswa._id.toString() === siswaId.toString());
    return siswa ? siswa.nama : 'Tidak Diketahui';
  };

  // Filter data based on the search query and date range
  const filteredData = dataRekap.filter(rekap => {
    const siswaName = getSiswaName(rekap.siswa_id).toLowerCase();
    const searchQueryLower = searchQuery.toLowerCase();

    const rekapDate = new Date(rekap.waktu_pembelajaran);
    const start = new Date(startDate);
    const end = new Date(endDate);

    const isInDateRange = (
      (!startDate || rekapDate >= start) &&
      (!endDate || rekapDate <= end)
    );

    return (
      isInDateRange &&
      (
        siswaName.includes(searchQueryLower) ||
        rekap.mata_pelajaran.toLowerCase().includes(searchQueryLower)
      )
    );
  });

  if (loading) {
    return <div className="flex m-4 justify-center items-center w-screen"><Mosaic color="#1177bc" size="large" text="Loading.." textColor="" /></div>
  }
  if (error) return <div>Error: {error}</div>;

  return (
    <>
      <div className="flex flex-col mt-16 mb-4">
        <div className="mb-4">
        <a href="/tentor/presensi/create">
          <button className="mr-4 bg-lightBlue-500 text-white active:bg-lightBlue-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150">
            <i className="fas fa-plus"></i>
          </button>
        </a>
        <input 
          type="text" 
          className="px-2 py-1 w-4 rounded-lg shadow space-x-2 mr-4 mt-2" 
          placeholder="Cari..."
          value={searchQuery}
          onChange={handleSearchChange}
        /> 
        </div>
        <span className="ml-2 mr-2 mt-2 font-red font-bold mb-4">Filter : </span>
        <div className="ml-4 mr-4 flex flex-col max-w-fit">
          <input 
            type="date"
            className="px-2 py-1 rounded-lg shadow"
            value={startDate}
            onChange={handleStartDateChange}
          />
          <span className="m-2 font-red font-bold">to</span>
          <input 
            type="date"
            className="px-2 py-1 rounded-lg shadow"
            value={endDate}
            onChange={handleEndDateChange}
          />
        </div>
      </div>
      <div className={`relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-white`}>
        <div className="rounded-t mb-0 px-4 py-3 border-0">
          <div className="flex flex-wrap items-center">
            <div className="relative w-full px-4 max-w-full flex-grow flex-1">
              <h3 className={`font-semibold text-lg text-blueGray-700`}>
                Presensi Table
              </h3>
            </div>
          </div>
        </div>
        <div className="block w-full overflow-x-auto">
          <table className="items-center w-full bg-transparent border-collapse">
            <thead>
              <tr>
                {["Nama Siswa", "Mata Pelajaran", "Waktu Pembelajaran", "Durasi", "Aksi"].map((heading, index) => (
                  <th
                    key={index}
                    className={`px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100`}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredData.map((rekap, index) => (
                <tr key={index}>
                  <td className="px-6 align-middle border border-solid py-3 text-xs whitespace-nowrap">
                    {getSiswaName(rekap.siswa_id)}
                  </td>
                  <td className="px-6 align-middle border border-solid py-3 text-xs whitespace-nowrap">
                    {rekap.mata_pelajaran}
                  </td>
                  <td className="px-6 align-middle border border-solid py-3 text-xs whitespace-nowrap">
                    {new Date(rekap.waktu_pembelajaran).toLocaleDateString()}
                  </td>
                  <td className="px-6 align-middle border border-solid py-3 text-xs whitespace-nowrap">
                    {rekap.durasi}
                  </td>
                  <td className="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-right">
                    <div className="relative inline-block text-left">
                      <div>
                        <button
                          type="button"
                          className="flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
                          onClick={() => handleDropdownToggle(rekap._id)}
                        >
                          <i className="fas fa-ellipsis-v"></i>
                        </button>
                      </div>

                      {openDropdownId === rekap._id && (
                        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded shadow-lg z-10">
                          <a
                            href={`/tentor/presensi/view/${rekap._id}`}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <i className="fas fa-eye mr-2"></i> View
                          </a>
                          <a
                            href={`/tentor/presensi/update/${rekap._id}`}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <i className="fas fa-pen mr-2"></i> Update
                          </a>
                          <button
                            onClick={() => handleDelete(rekap._id)}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                          >
                            <i className="fas fa-trash mr-2"></i> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

CardTable.propTypes = {
  color: PropTypes.oneOf(["light", "dark"]).isRequired,
};
